import { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression'; // Import the library
import { renameToMatchType } from '../components/fileTypeNaming';

/**
 * Custom hook for managing image state in training forms
 * @returns {Object} Image state and handler functions
 */
export const useImageHandling = () => {
  const [images, setImages] = useState({});
  const [previews, setPreviews] = useState({});

  // Update an image with a new file
  const updateImage = async (index, file, preservePreview = false) => { // Make the function async
    if (!file) return;

    console.log(`updateImage called for index ${index} with file ${file.name} (size: ${file.size}, type: ${file.type}), preservePreview: ${preservePreview}`);

    let fileToProcess = file; // This will be the file we store, potentially compressed

    // Attempt to compress large images, as per the provided example.
    if (file.type.startsWith('image/') && file.size > 250 * 1024) {
      try {
        console.log(`Attempting to compress ${file.name}, original size: ${Math.round(file.size / 1024)}KB. Target <100KB.`);
        const options = {
          maxSizeMB: 0.1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          initialQuality: 0.7,
          alwaysKeepResolution: false,
          fileType: 'image/jpeg', // Forces output to JPEG as per example
        };
        const compressedBlob = await imageCompression(file, options); // imageCompression returns a Blob
        const compressedSizeKB = Math.round(compressedBlob.size / 1024);
        console.log(`Compression result for ${file.name}: new size: ${compressedSizeKB}KB. Original type: ${file.type}, New type: ${compressedBlob.type}`);

        // Only use the compressed file if it's actually smaller
        if (compressedBlob.size < file.size) {
          // Compression re-encodes to options.fileType, so the original
          // extension would now describe the wrong bytes — a compressed
          // chart.png holds JPEG data. The server matches the extension against
          // the declared mimetype and Azure types the blob from the extension,
          // so the name has to follow the new type. Same fix as FileUpload.jsx.
          const compressedType = compressedBlob.type || 'image/jpeg';
          const finalName = renameToMatchType(file.name, compressedType);
          fileToProcess = new File([compressedBlob], finalName, { type: compressedType, lastModified: Date.now() });
          console.log(`Using compressed file for ${file.name}: ${fileToProcess.name}, new size: ${compressedSizeKB}KB, new type: ${fileToProcess.type}`);
          
          // Warn if the compressed file (which is now fileToProcess) is still larger than 75KB
          if (fileToProcess.size > 75 * 1024) {
            console.warn(`WARN: Compressed file ${fileToProcess.name} is still ${compressedSizeKB}KB.`);
          }
        } else {
          console.warn(`Compressed file for ${file.name} is not smaller than original (original: ${Math.round(file.size / 1024)}KB, compressed: ${compressedSizeKB}KB). Using original file.`);
          // fileToProcess remains the original `file`
        }
      } catch (compressionError) {
        console.error(`Error during image compression for ${file.name}:`, compressionError);
        // Fallback to original file if compression fails. fileToProcess is already `file`.
        console.log(`Using original file ${file.name} due to compression error.`);
      }
    } else if (file.type.startsWith('image/')) {
      console.log(`File ${file.name} (size: ${Math.round(file.size/1024)}KB) is not larger than 250KB. Using original file.`);
      // fileToProcess remains the original `file`
    } else {
      console.log(`File ${file.name} is not an image or does not meet compression criteria. Using original file.`);
      // fileToProcess remains the original `file`
    }
    
    // Use a numeric index to ensure consistent handling
    const numIndex = parseInt(index);
    
    // Update the file in state with the (potentially compressed) fileToProcess
    setImages(prev => ({
      ...prev,
      [numIndex]: fileToProcess
    }));
    
    // Only create a new preview URL if we're not preserving the existing one
    if (!preservePreview) {
      // Revoke previous URL if it's a blob URL
      if (previews[numIndex] && previews[numIndex].startsWith('blob:')) {
        URL.revokeObjectURL(previews[numIndex]);
      }
      
      // Create new preview URL from the (potentially compressed) fileToProcess
      const previewUrl = URL.createObjectURL(fileToProcess);
      setPreviews(prev => ({
        ...prev,
        [numIndex]: previewUrl
      }));
      
      console.log(`Created new preview URL for index ${numIndex} from file ${fileToProcess.name}`);
    } else {
      console.log(`Preserved existing preview URL for index ${numIndex}: ${previews[numIndex]}`);
    }
  };

  // Remove an image
  const removeImage = (index) => {
    // Remove the image from state
    setImages(prev => {
      const newImages = {...prev};
      delete newImages[index];
      return newImages;
    });
    
    // Revoke the preview URL and remove it from state
    if (previews[index]) {
      if (previews[index].startsWith('blob:')) {
        URL.revokeObjectURL(previews[index]);
      }
      setPreviews(prev => {
        const newPreviews = {...prev};
        delete newPreviews[index];
        return newPreviews;
      });
    }
  };

  // Set preview URL directly (for existing images)
  const setPreviewUrl = (index, url) => {
    // Check if we're replacing an existing blob URL
    if (previews[index] && previews[index].startsWith('blob:')) {
      URL.revokeObjectURL(previews[index]);
    }
    
    setPreviews(prev => ({
      ...prev,
      [index]: url
    }));
  };

  // Clear all images
  const clearAllImages = () => {
    // Revoke all blob preview URLs
    Object.values(previews).forEach(url => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    
    // Reset state
    setImages({});
    setPreviews({});
  };
  
  // Update all images at once - useful for reindexing
  const updateAllImagesAndPreviews = (newImages, newPreviews) => {
    const newPreviewUrls = new Set(Object.values(newPreviews).filter(url => url && url.startsWith('blob:')));

    // Revoke any replaced blob URLs to prevent memory leaks
    Object.entries(previews).forEach(([index, url]) => {
      // If an existing blob URL is not found in the set of new blob URLs, revoke it.
      if (url && url.startsWith('blob:') && !newPreviewUrls.has(url)) {
        URL.revokeObjectURL(url);
      }
    });

    
    // Set the new state all at once
    setImages(newImages);
    setPreviews(newPreviews);
  };

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      // Revoke all blob preview URLs
      Object.values(previews).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previews]); // Keep previews as dependency

  return {
    images,
    previews,
    updateImage,
    removeImage,
    setPreviewUrl,
    clearAllImages,
    updateAllImagesAndPreviews
  };
};

/**
 * Simplifies clearing all image states in components not using the hook
 * @param {Object} params - Object containing all required parameters
 */
export const clearAllImageStates = ({
  questionImagePreviews = {},
  explanationImagePreviews = {},
  setQuestionImages,
  setExplanationImages,
  setQuestionImagePreviews,
  setExplanationImagePreviews
}) => {
  // Reset state objects
  if (setQuestionImages) setQuestionImages({});
  if (setExplanationImages) setExplanationImages({});
  
  // Release object URLs to avoid memory leaks
  Object.values(questionImagePreviews).forEach(url => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url); // check if starts with blob:
  });
  Object.values(explanationImagePreviews).forEach(url => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url); // check if starts with blob:
  });
  
  // Reset preview state
  if (setQuestionImagePreviews) setQuestionImagePreviews({});
  if (setExplanationImagePreviews) setExplanationImagePreviews({});
};

/**
 * Gets the client view URL for the selected test
 * @param {string} hospitalId - ID of the selected hospital
 * @param {string} topicId - ID of the selected topic
 * @param {string} testId - ID of the selected test (optional)
 * @returns {string} URL for client view
 */
export const getClientViewUrl = (hospitalId, topicId, testId) => {
  if (!hospitalId || !topicId) {
    return '#';
  }
  
  const hostname = window.location.hostname;
  let baseUrl;
  
  if (hostname.includes('localhost')) {
    baseUrl = 'http://localhost:5173';
  } else if (hostname.includes('stg')) {
    baseUrl = 'https://trainings.mashlom.me/staging';
  } else {
    // Assuming production if not localhost or stg
    baseUrl = 'https://trainings.mashlom.me'; // Corrected production URL
  }
  
  const topicForUrl = encodeURIComponent(topicId);
  let url = `${baseUrl}/#/training-topic/${topicForUrl}`;
  
  if (testId) {
    url += `/test/${encodeURIComponent(testId)}`;
  }
  
  return url;
};